import React, { useState } from 'react';

const AIDataTable = ({ title, tableData }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Validate input data
  const validTableData = Array.isArray(tableData) && tableData.length > 0 ? tableData : [];
  
  // Sort data based on current sort configuration (must be called before any conditional returns)
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return validTableData;

    const sorted = [...validTableData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // String comparison
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      
      if (aString < bString) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aString > bString) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [validTableData, sortConfig]);

  // If no data, show empty state (after hooks are called)
  if (validTableData.length === 0) {
    return (
      <div className="ai-chart-wrapper">
        <h4 className="ai-chart-title">{title}</h4>
        <div className="ai-chart-empty">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  // Extract column headers from first row
  const headers = Object.keys(validTableData[0]);

  // Handle column header click for sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator for column
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return <span className="ai-table-sort-icon">⇅</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <span className="ai-table-sort-icon active">▲</span> : 
      <span className="ai-table-sort-icon active">▼</span>;
  };

  // Format cell value for display
  const formatCellValue = (value) => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  // Format header name (convert snake_case to Title Case)
  const formatHeaderName = (header) => {
    return header
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="ai-chart-wrapper">
      <h4 className="ai-chart-title">{title}</h4>
      <div className="ai-table-container">
        <table className="ai-data-table">
          <thead>
            <tr>
              {headers.map((header) => (
                <th 
                  key={header}
                  onClick={() => handleSort(header)}
                  className="ai-table-header"
                  title={`Click to sort by ${formatHeaderName(header)}`}
                >
                  <div className="ai-table-header-content">
                    <span>{formatHeaderName(header)}</span>
                    {getSortIndicator(header)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => (
              <tr key={rowIndex} className="ai-table-row">
                {headers.map((header) => (
                  <td key={`${rowIndex}-${header}`} className="ai-table-cell">
                    {formatCellValue(row[header])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ai-table-footer">
        <span className="ai-table-row-count">
          Showing {sortedData.length} row{sortedData.length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export default AIDataTable;
